import React, { useEffect, useRef, useState } from 'react'
import styles from './Highlight.module.css'
import { useNavigate } from 'react-router-dom'
import gsap from 'gsap'

export type HighlightProps = {
    setFilterProduct: React.Dispatch<React.SetStateAction<number | null>>;
}

const Highlight = (props: HighlightProps) => {
    const [currentType, setCurrentType] = useState(1)
    const highlightsRef = useRef<HTMLDivElement>(null)
    const isAnimating = useRef(false)
    const verticalLock = useRef(true)
    const unlockTimeout = useRef<NodeJS.Timeout | null>(null)
    const navigate = useNavigate();

    const handleFilterProducts = (id: number) => {
        if (!id) return;
        props.setFilterProduct(id);
        navigate("/produtos");
    }

    const scrollToType = (index: number) => {
        const container = highlightsRef.current
        if (!container) return

        const target = container.children[index] as HTMLElement | undefined
        if (!target) return

        isAnimating.current = true
        verticalLock.current = true

        const containerWidth = container.clientWidth
        const targetMid = target.offsetLeft + target.offsetWidth / 2
        const scrollTo = targetMid - containerWidth / 2

        container.scrollTo({ left: scrollTo, behavior: 'smooth' })
        setCurrentType(index)

        setTimeout(() => {
            isAnimating.current = false
            if (unlockTimeout.current) clearTimeout(unlockTimeout.current)
            unlockTimeout.current = setTimeout(() => {
                verticalLock.current = false
            }, 300)
        }, 500)
    }

    useEffect(() => {
        const container = highlightsRef.current
        if (!container) return

        const handleWheel = (e: WheelEvent) => {
            if (isAnimating.current) {
                e.preventDefault()
                return
            }

            const maxIndex = container.children.length - 1
            const atFirst = currentType === 0
            const atLast = currentType === maxIndex

            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                if ((e.deltaY > 0 && !atLast) || (e.deltaY < 0 && !atFirst)) {
                    e.preventDefault()
                    if (e.deltaY > 0) scrollToType(currentType + 1)
                    else scrollToType(currentType - 1)
                } else if (!verticalLock.current) {
                    verticalLock.current = true
                    setTimeout(() => {
                        verticalLock.current = false
                    }, 300)
                } else {
                    e.preventDefault()
                }
            }
        }

        container.addEventListener('wheel', handleWheel, { passive: false })
        return () => container.removeEventListener('wheel', handleWheel)
    }, [currentType])

    useEffect(() => {
        scrollToType(currentType)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // helper for hover infinite zoom
    const handleHover = (wrapper: HTMLDivElement | null, enter: boolean) => {
        if (!wrapper) return
        const img = wrapper.querySelector(`.${styles.imageHl}`) as HTMLElement
        if (!img) return

        if (enter) {
            gsap.to(img, {
                scale: 1.2,
                duration: 5,
                ease: "power1.inOut",
                yoyo: true,
                repeat: -1,
            })
        } else {
            gsap.killTweensOf(img)
            gsap.to(img, {
                scale: 1,
                duration: 0.8,
                ease: "power2.inOut",
            })
        }
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <span className={styles.title}>
                    Três categorias<br />Inúmeras possibilidades
                </span>

                <div className={styles.types}>
                    {['Pavimentos Interior XL', 'Louças Sanitárias', 'Revestimentos WPC'].map((label, i) => (
                        <span
                            key={i}
                            className={currentType === i ? styles.active : ''}
                            onClick={() => scrollToType(i)}
                        >
                            {label}
                        </span>
                    ))}
                </div>

                <div className={styles.highlights} ref={highlightsRef}>
                    {/* Highlight 1 */}
                    <div className={styles.highlight} onClick={() => handleFilterProducts(17)}>
                        <div
                            className={styles.imageWrapper}
                            onMouseEnter={(e) => handleHover(e.currentTarget, true)}
                            onMouseLeave={(e) => handleHover(e.currentTarget, false)}
                        >
                            <div
                                className={styles.imageHl}
                                style={{ backgroundImage: `url('/assets/images/pavimento_interior_XL.jpg')` }}
                            ></div>
                        </div>
                        <span>
                            Pavimentos cerâmicos XL para interiores sofisticados. Formatos amplos que criam ambientes contínuos, elegantes e modernos.
                        </span>
                    </div>

                    {/* Highlight 2 */}
                    <div className={styles.highlight} onClick={() => handleFilterProducts(10)}>
                        <div
                            className={styles.imageWrapper}
                            onMouseEnter={(e) => handleHover(e.currentTarget, true)}
                            onMouseLeave={(e) => handleHover(e.currentTarget, false)}
                        >
                            <div
                                className={`${styles.imageHl} ${styles.img_sanitarias}`}
                                style={{ backgroundImage: `url('/assets/images/Louças Sanitarias.jpg')` }}
                            ></div>
                        </div>
                        <span>
                            Cerâmicas para cozinhas, casas de banho e interiores modernos. Versatilidade, textura e design para todos os estilos de espaço.
                        </span>
                    </div>

                    {/* Highlight 3 */}
                    <div className={styles.highlight} onClick={() => handleFilterProducts(16)}>
                        <div
                            className={styles.imageWrapper}
                            onMouseEnter={(e) => handleHover(e.currentTarget, true)}
                            onMouseLeave={(e) => handleHover(e.currentTarget, false)}
                        >
                            <div
                                className={styles.imageHl}
                                style={{ backgroundImage: `url('/assets/images/revestimento wpc.jpg')` }}
                            ></div>
                        </div>
                        <span>
                            Revestimentos WPC para pavimentos exteriores duradouros. A estética da madeira com a resistência e sustentabilidade do compósito.
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Highlight